import { createSlice } from '@reduxjs/toolkit'

import { actions as localActionsF } from './local'
import { actions as surveyActionsF } from './surveys'
import { actions as actionActionsF } from './actions'
import orderActions from 'FlowTaker/lib/orderActions'
import { toast } from 'react-toastify'
import { deepSnakeCase } from 'Shared/functions/util'

const reducer = createSlice({
  name: 'builder',
  initialState: {
    templateFields: {},
    validationErrors: [],
    templateValidationErrors: []
  },
  reducers: {
    set: (s, a) => a.payload,
    upsert: (s, a) => ({ ...s, ...a.payload }),
    setTemplateField: (s, a) => {
      s.flow.templateFields[a.payload.slug] = a.payload.value
    },
    updateFlowSetting: (s, a) => {
      s.flow.settings[a.payload.key] = a.payload.value
    },
    updateFlowTextContent: (s, a) => {
      s.flow.textContent ||= {}
      s.flow.textContent[a.payload.language] ||= {}
      s.flow.textContent[a.payload.language][a.payload.key] = a.payload.value
    },
    upsertFlowTemplate: (s, a) => {
      s.flowTemplate = { ...s.flowTemplate, ...a.payload }
    },
    updateFlow: (s, a) => {
      s.flow[a.payload.key] = a.payload.value
    },
    updateFlowContentTest: (s, a) => {
      const { slug, contentVariantId, language, key, value } = a.payload
      const contentTest = s.flow[slug]
      const variant = contentTest.contentVariants.find(cv => cv.id === contentVariantId)
      variant.content.textContent ||= {}
      variant.content.textContent[language] ||= {}
      variant.content.textContent[language][key] = value
    },
    addFlowContentTestVariant: (s, a) => {
      const { slug } = a.payload
      const contentTest = s.flow[slug]
      const maxIndex = Math.max(-1, ...contentTest.contentVariants.map(cv => cv.content?.index ?? -1))
      const newVariant = {
        id: uuidv4(),
        content: { textContent: {}, channels: ['all'], index: maxIndex + 1 }
      }
      contentTest.contentVariants.push(newVariant)
    },
    duplicateFlowContentTestVariant: (s, a) => {
      const { slug, contentVariantId } = a.payload
      const contentTest = s.flow[slug]
      const source = contentTest.contentVariants.find(cv => cv.id === contentVariantId)
      const maxIndex = Math.max(-1, ...contentTest.contentVariants.map(cv => cv.content?.index ?? -1))
      const newVariant = {
        id: uuidv4(),
        content: { ...JSON.parse(JSON.stringify(source.content)), index: maxIndex + 1 }
      }
      contentTest.contentVariants.push(newVariant)
    },
    updateFlowContentTestChannels: (s, a) => {
      const { slug, contentVariantId, channels } = a.payload
      const contentTest = s.flow[slug]
      const contentVariant = contentTest.contentVariants.find(cv => cv.id === contentVariantId)
      contentVariant.content.channels = channels
    },
    deactivateFlowContentTestVariant: (s, a) => {
      const { slug, contentVariantId } = a.payload
      const contentTest = s.flow[slug]
      const contentVariant = contentTest.contentVariants.find(cv => cv.id === contentVariantId)
      contentVariant.active = false
    },
    setValidationErrors: (s, a) => {
      s.validationErrors = a.payload
    },
    setSetupValidationErrors: (s, a) => {
      s.setupValidationErrors = a.payload
    },
    setTemplateValidationErrors: (s, a) => {
      s.templateValidationErrors = a.payload
    },
  },
}).reducer

const actions = (dis, store, restClient) => {
  const localActions = localActionsF(dis, store, restClient)
  const surveyActions = surveyActionsF(dis, store, restClient)
  const actionActions = actionActionsF(dis, store, restClient)

  function load({ flowId, flowTemplateId, isEditingTemplate = false, nav }) {
    localActions.upsert({ flowId, flowTemplateId, isEditingTemplate })

    if (nav) localActions.setNav(nav)
    if (isEditingTemplate) {
      localActions.setNav('template')
    }

    return loadData()
  }

  function loadData() {
    localActions.upsert({ loaded: false })
    const state = store.getState()
    const { flowId, flowTemplateId } = state.local

    const promises = []
    promises.push(loadFlow(flowId))
    promises.push(loadFlowTemplate(flowTemplateId))
    promises.push(loadMergeTags())
    return Promise.all(promises).then(() => localActions.upsert({ loaded: true }))
  }

  function loadFlow(flowId) {
    return restClient.get(`/api/flows/${flowId}`)
      .then(({ data: flow }) => {
        handleFlowData(flow)
        selectAction(flow.actions.find(a => a.isStart)?.id)
      })
  }

  function loadFlowTemplate(flowTemplateId) {
    return restClient.get(`/api/flow_templates/${flowTemplateId}`)
      .then(({ data: flowTemplate }) => {
        handleFlowTemplateData(flowTemplate)
      })
  }

  function handleFlowData(flow) {
    // Fields which are merged in Liquid templates need to be snake cased
    const templateFields = deepSnakeCase(flow.templateFields)
    const flowFields = deepSnakeCase(flow.flowFields)

    const snakeFlow = { ...flow, templateFields, flowFields }

    // Backfill index for content variants that predate the index field
    if (snakeFlow.shareContentTest?.contentVariants) {
      snakeFlow.shareContentTest.contentVariants.forEach((cv, i) => {
        if (cv.content && cv.content.index === undefined) {
          cv.content.index = i
        }
      })
    }

    dis({ type: 'builder/upsert', payload: { flow: snakeFlow } })
    flow.actions.forEach(a => {
      if (a.kind === 'survey') {
        surveyActions.load(a.surveyId)
      }
    })

    dis({ type: 'actions/setAll', payload: flow.actions })
    actionActions.ensureStartingOrder()
    dis({ type: 'actionTemplates/setAll', payload: _.flatMap(flow.actions, action => action.actionTemplates || []) })

    localActions.setDirty(false)
  }

  function handleFlowTemplateData(flowTemplate) {
    dis({ type: 'builder/upsert', payload: { flowTemplate } })
  }

  function loadMergeTags() {
    return restClient.get(`/api/render/available_merge_tags`)
      .then(({ data: mergeTags }) => {
        dis({ type: 'builder/upsert', payload: { mergeTags } })
      })
  }

  function selectAction(actionId, { pageNumber } = {}) {
    const state = store.getState()
    if (state.local.navItem === 'setup') {
      actionId = null
    }
    if (typeof(pageNumber) === 'undefined') {
      if (state.local.selectedActionId === actionId) {
        var selectedPageNumber = state.local.selectedPageNumber
      } else {
        var selectedPageNumber = 1
      }
    } else {
      var selectedPageNumber = pageNumber
    }

    dis({ type: 'local/upsert', payload: { selectedActionId: actionId, selectedPageNumber } })
  }

  function selectQuestion(questionId) {
    dis({ type: 'local/upsert', payload: { selectedQuestionId: questionId } })
  }

  function setTemplateField(payload) {
    localActions.setDirty(true)
    dis({ type: 'builder/setTemplateField', payload })
  }

  function updateFlowSetting(key, value) {
    localActions.setDirty(true)
    dis({ type: 'builder/updateFlowSetting', payload: { key, value } })
  }

  function updateFlowTextContent(language, key, value) {
    localActions.setDirty(true)
    dis({ type: 'builder/updateFlowTextContent', payload: { language, key, value } })
  }

  function updateFlowContentTest({ slug, contentVariantId, key, value, language }) {
    localActions.setDirty(true)
    dis({ type: 'builder/updateFlowContentTest', payload: { slug, contentVariantId, key, value, language } })
  }

  function addFlowContentTestVariant(slug) {
    localActions.setDirty(true)
    dis({ type: 'builder/addFlowContentTestVariant', payload: { slug } })
  }

  function duplicateFlowContentTestVariant(slug, contentVariantId) {
    localActions.setDirty(true)
    dis({ type: 'builder/duplicateFlowContentTestVariant', payload: { slug, contentVariantId } })
  }

  function updateFlowContentTestChannels({ slug, contentVariantId, channels }) {
    localActions.setDirty(true)
    dis({ type: 'builder/updateFlowContentTestChannels', payload: { slug, contentVariantId, channels } })
  }

  function deactivateFlowContentTestVariant(slug, contentVariantId) {
    localActions.setDirty(true)
    dis({ type: 'builder/deactivateFlowContentTestVariant', payload: { slug, contentVariantId } })
  }

  function updateFlow(key, value) {
    localActions.setDirty(true)
    dis({ type: 'builder/updateFlow', payload: { key, value } })
  }

  function updateFlowLanguages(defaultLanguage, editableLanguages, publishedLanguages) {
    let editLang = [...editableLanguages]
    let pubLang = [...publishedLanguages]

    if (!editLang.includes(defaultLanguage)) {
      editLang.push(defaultLanguage)
    }
    if (!pubLang.includes(defaultLanguage)) {
      pubLang.push(defaultLanguage)
    }
    pubLang.forEach(l => {
      if (!editLang.includes(l)) {
        editLang.push(l)
      }
    })
    localActions.setDirty(true)

    dis({ type: 'builder/updateFlow', payload: { key: 'defaultLanguage', value: defaultLanguage } })
    dis({ type: 'builder/updateFlow', payload: { key: 'editableLanguages', value: editLang } })
    dis({ type: 'builder/updateFlow', payload: { key: 'publishedLanguages', value: pubLang } })
  }

  function upsertFlowTemplate(payload) {
    dis({ type: 'builder/upsertFlowTemplate', payload })
  }

  function persistAll() {
    const state = store.getState()
    const entities = state.actions.entities
    const orderedActions = orderActions(_.values(entities))
    const currentActionId = state.local.selectedActionId
    const isEditingTemplate = state.local.isEditingTemplate
    const currentActionsIndex = orderedActions.findIndex(a => a.id === currentActionId)
    const actions = _.values(state.actions.entities)

    const promises = []
    actions.forEach(action => {
      if (action.kind === 'survey' && action.surveyId) {
        promises.push(surveyActions.persistQuestions(action.surveyId))
      }
    })
    promises.push(persistFlow())
    if (isEditingTemplate) promises.push(persistFlowTemplate())

    return Promise.all(promises)
      .then(() => localActions.setDirty(false))
      .then(() => {
        const currentState = store.getState()
        const entities = currentState.actions.entities
        const orderedActions = orderActions(_.values(entities))
        const currentActionId = currentState.local.selectedActionId

        if (!entities[currentActionId]) {
          const lastActionId = orderedActions[currentActionsIndex]?.id
          return selectAction(lastActionId)
        }
        return selectAction(currentActionId)
      })
  }

  const persistFlowFields = ['name', 'slug', 'effortId', 'settings', 'templateFields', 'additionalHead', 'additionalCss', 'textContent', 'defaultLanguage', 'editableLanguages', 'publishedLanguages', 'shareContentTest', 'shareUrl']

  function persistFlow() {
    const state = store.getState()
    const flow = _.pick(state.builder.flow, persistFlowFields)
    const actions = _.values(state.actions.entities).map(action => {
      const newAction = { ...action }
      if (newAction.kind === 'email_to_target') {
          newAction.actionTemplates = _.values(state.actionTemplates.entities).filter(at => at.actionId === action.id)
      }
      newAction.config = _.omit(newAction.config, ['customDecisionMakerSetId', 'inbuiltDecisionMakerSetId'])

      return newAction
    })
    const flowId = state.builder.flow.id

    return restClient.put(`/api/flows/${flowId}`, { flow, actions })
      .then((response) => {
        dis({ type: 'local/clearErrors', payload: 'flow' })
        handleFlowData(response.data)
      }).catch((error) => {
        console.error('Error saving flow:', error)
        toast.error('Failed to save. Please check the details you entered')
        dis({ type: 'local/upsert', payload: { errors: error.response.data.errors } })
      })
  }

  function persistFlowTemplate() {
    const state = store.getState()
    const flowTemplate = state.builder.flowTemplate
    const flowTemplateId = flowTemplate.id

    const flowTemplateParams = _.pick(flowTemplate, ['name', 'description', 'enabledForNewPages', 'bodyHtml', 'headHtml', 'stylesCss', 'fields', 'additionalHead', 'additionalCss'])
    return restClient.put(`/api/flow_templates/${flowTemplateId}`, { flowTemplate: flowTemplateParams })
      .then((response) => {
        dis({ type: 'local/clearErrors', payload: 'flowTemplate' })
        handleFlowTemplateData(response.data)
      }).catch((error) => {
        toast.error('Failed to save. Please check the details you entered')
        dis({ type: 'local/upsert', payload: { errors: error.response.data.errors } })
      })
  }

  function setValidationErrors(errors) {
    dis({ type: 'builder/setValidationErrors', payload: errors })
  }

  function setSetupValidationErrors(errors) {
    dis({ type: 'builder/setSetupValidationErrors', payload: errors })
  }

  function setTemplateValidationErrors(errors) {
    dis({ type: 'builder/setTemplateValidationErrors', payload: errors })
  }

  function cloneFlowTemplate(name) {
    const state = store.getState()
    const flowTemplateId = state.builder.flowTemplate.id

    return restClient.post(`/api/flow_templates/${flowTemplateId}/clone`, {
      flow_template: {name}
    })
    .then(({ data: flowTemplate }) => {
      window.location.href = `/admin/settings/flow_templates/${flowTemplate.id}/edit`
    })
    .catch((error) => {
      toast.error('Failed to clone template. Please try again.')
      dis({ type: 'local/upsert', payload: { errors: error.response.data.errors } })
    })
  }

  let latestHash = null
  let pendingPostMessage = null
  const debouncedPostMessage = _.debounce((iframe, payload) => {
    pendingPostMessage = null
    iframe.contentWindow.postMessage({ type: 'FLOW_DATA', payload }, '*')
  }, 100)

  function syncPreview({ iframe, force = false }) {
    if (!iframe) return

    if (force) {
      debouncedPostMessage.cancel()
    }

    const state = store.getState()

    const orderedActions = orderActions(_.values(state.actions.entities))
    const firstActionId = orderedActions[0]?.id

    const selectedActionId = state.local.selectedActionId || firstActionId
    const flow = state.builder.flow
    const actions = _.values(state.actions.entities) || []
    const surveys = _.values(state.surveys.entities) || []
    const questions = _.values(state.questions.entities) || []
    const answers = _.values(state.answers.entities) || []
    const flowTemplate = state.builder.flowTemplate || {}
    const language = state.local.language

    const selectedAction = actions.find(a => a.id === selectedActionId) || {}
    if (selectedAction.kind === 'redirect') return // Don't send redirect actions to the preview

    const payload = {
      flow: { ...flow, actions },
      flowTaker: {
        currentActionId: selectedActionId,
        currentPageNumber: state.local.selectedPageNumber
      },
      flowTemplate,
      targetDetails: {},
      surveys,
      questions,
      answers,
      language
    }
    payload.flow.publishedLanguages = payload.flow.editableLanguages // To ensure we can preview as any editable language

    // Additional payload for template editor
    if (!flowTemplate.systemTemplate) {
      payload.flow.template = {
        styles: flowTemplate.stylesCss || '',
        head: flowTemplate.headHtml || '',
        body: flowTemplate.bodyHtml || '',
      }
    }

    // Stop duplicate updates
    const newHash = JSON.stringify(payload)
    if (newHash === latestHash && !force) return
    latestHash = newHash

    debouncedPostMessage(iframe, payload)
  }


  function fetchMetatags() {
    const state = store.getState()
    let shareUrl = (state.builder?.flow?.shareUrl || '').trim()
    const defaultLanguage = state.builder?.flow?.defaultLanguage || 'en'
    const contentVariants = (state.builder?.flow?.shareContentTest?.contentVariants || [])
      .filter(cv => cv.active !== false)

    if (!shareUrl || contentVariants.length !== 1) return Promise.resolve()

    if (!/^https?:\/\//i.test(shareUrl)) {
      shareUrl = `https://${shareUrl}`
      updateFlow('shareUrl', shareUrl)
    }

    const variant = contentVariants[0]
    return restClient.get('/api/flows/fetch_metatags', { params: { url: shareUrl } })
      .then(({ data }) => {
        if (data.title) {
          updateFlowContentTest({ slug: 'shareContentTest', contentVariantId: variant.id, key: 'shareTitle', value: data.title, language: defaultLanguage })
        }
        if (data.description) {
          updateFlowContentTest({ slug: 'shareContentTest', contentVariantId: variant.id, key: 'shareDescription', value: data.description, language: defaultLanguage })
        }
        if (data.imageUrl) {
          updateFlowContentTest({ slug: 'shareContentTest', contentVariantId: variant.id, key: 'shareImageUrl', value: data.imageUrl, language: defaultLanguage })
        }
      })
  }

  return {
    load,
    loadData,
    selectAction,
    selectQuestion,
    setTemplateField,
    updateFlowSetting,
    updateFlowTextContent,
    updateFlowContentTest,
    updateFlowContentTestChannels,
    addFlowContentTestVariant,
    duplicateFlowContentTestVariant,
    deactivateFlowContentTestVariant,
    updateFlow,
    updateFlowLanguages,
    upsertFlowTemplate,
    persistAll,
    setValidationErrors,
    setSetupValidationErrors,
    setTemplateValidationErrors,
    cloneFlowTemplate,
    syncPreview,
    fetchMetatags
  }
}

export { reducer, actions }
export default { reducer, actions }
